import { AxiosError } from 'axios'

import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query'

import APIServices from '../../api/APIServices'

// EconomicCycle type based on API response
export interface EconomicCycle {
  id: number
  name: string
  observations: string | null
  openDate: string
  closedDate: string | null
  priceSystemId: number
  isActive: boolean
  openBy: {
    username: string
    email: string
    displayName: string
    avatar: {
      id: number
      src: string
      thumbnail: string
      blurHash: string
    }
  }
  meta: string
  closedBy: {
    username: string
    email: string
    displayName: string
    avatar: {
      id: number
      src: string
    }
  }
  priceSystem: {
    id: number
    name: string
    isMain: boolean
  }
}

export interface EconomicCyclesQueryParams {
  page?: number
  per_page?: number
  dateFrom?: string | null
  dateTo?: string | null
}

export interface EconomicCyclesResponse {
  items: EconomicCycle[]
  totalItems: number
  currentPage: number
  totalPages: number
}

// Interface for the query key parameters
export interface EconomicCyclesQueryKeyParams {
  businessId: number
  dateFrom?: string
  dateTo?: string
  economicCycleId?: number
  page?: number
  per_page?: number
}

// Interface for the hook parameters
export interface UseEconomicCyclesQueryParams {
  params?: EconomicCyclesQueryParams
  options?: Partial<UseQueryOptions<EconomicCyclesResponse, AxiosError>>
  businessId: number
}

// Interface for the infinite query hook parameters
export interface UseInfiniteEconomicCyclesQueryParams {
  businessId: number
  params?: EconomicCyclesQueryParams
  options?: Partial<
    UseInfiniteQueryOptions<
      EconomicCyclesResponse,
      AxiosError,
      EconomicCyclesResponse,
      EconomicCyclesResponse,
      [string, EconomicCyclesQueryKeyParams],
      number
    >
  >
}

// Interface for the return value of useEconomicCyclesQuery
export interface UseEconomicCyclesQueryReturn {
  economicCycles: EconomicCycle[]
  isLoadingEconomicCycles: boolean
  isErrorEconomicCycles: boolean
  errorEconomicCycles: AxiosError | null
  refetchEconomicCycles: () => void
  isFetchingEconomicCycles: boolean
  totalEconomicCycles: number
  currentPage: number
  totalPages: number
}

// Interface for the return value of useInfiniteEconomicCyclesQuery
export interface UseInfiniteEconomicCyclesQueryReturn {
  economicCycles: EconomicCycle[]
  totalItems: number
  loadedCount: number
  hasNextPage: boolean
  isLoading: boolean
  isLoadingMore: boolean
  isError: boolean
  error: AxiosError | null
  fetchNextPage: () => void
  refetch: () => void
}

const ECONOMIC_CYCLES_QUERY_KEY = 'economic-cycles'

const fetchEconomicCycles = async (
  params?: EconomicCyclesQueryParams
): Promise<EconomicCyclesResponse> => {
  const cleanParams = Object.fromEntries(
    Object.entries(params || {}).filter(([, value]) => value != null)
  )

  const response = await APIServices.get('/administration/economiccycle', {
    params: cleanParams,
  })

  return response.data
}

export const useEconomicCyclesQuery = ({
  params,
  options,
  businessId,
}: UseEconomicCyclesQueryParams): UseEconomicCyclesQueryReturn => {
  const queryResult = useQuery<EconomicCyclesResponse, AxiosError>({
    queryKey: getEconomicCyclesQueryKey({
      businessId,
      dateFrom: params?.dateFrom ?? undefined,
      dateTo: params?.dateTo ?? undefined,
      economicCycleId: params?.economicCycleId ?? undefined,
      page: params?.page,
      per_page: params?.per_page,
    }),
    queryFn: () => fetchEconomicCycles(params),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    enabled: !!businessId,
    ...options,
  })

  return {
    economicCycles: queryResult.data?.items || [],
    isLoadingEconomicCycles: queryResult.isLoading,
    isErrorEconomicCycles: queryResult.isError,
    errorEconomicCycles: queryResult.error,
    refetchEconomicCycles: queryResult.refetch,
    isFetchingEconomicCycles: queryResult.isFetching,
    totalEconomicCycles: queryResult.data?.totalItems || 0,
    currentPage: queryResult.data?.currentPage || 1,
    totalPages: queryResult.data?.totalPages || 1,
  }
}

/**
 * Custom hook for infinite scrolling economic cycles with date filtering
 * Following TanStack Query's infinite query pattern with optimized caching
 *
 * @param businessId - Business ID for scoping
 * @param params - Query parameters including date range
 * @param options - TanStack Query options for customizing query behavior
 * @returns Object containing economic cycles, loading states, and utility functions
 */
export const useInfiniteEconomicCyclesQuery = ({
  businessId,
  params = {},
  options,
}: UseInfiniteEconomicCyclesQueryParams): UseInfiniteEconomicCyclesQueryReturn => {
  const queryResult = useInfiniteQuery<
    EconomicCyclesResponse,
    AxiosError,
    EconomicCyclesResponse,
    [string, EconomicCyclesQueryKeyParams],
    number
  >({
    queryKey: getEconomicCyclesQueryKey({
      businessId,
      dateFrom: params?.dateFrom ?? undefined,
      dateTo: params?.dateTo ?? undefined,
      economicCycleId: params?.economicCycleId ?? undefined,
    }),
    queryFn: async ({ pageParam }) => {
      const result = await fetchEconomicCycles({
        ...params,
        page: pageParam,
        per_page: 5, // Small page size for cards
      })
      return result
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage.currentPage < lastPage.totalPages
        ? lastPage.currentPage + 1
        : undefined
    },
    refetchOnWindowFocus: false,
    // Keep data fresh for 5 minutes (economic cycles don't change frequently)
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Keep inactive data in cache for 30 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    // Keep previous data while fetching new date ranges (better UX)
    placeholderData: (previousData) => previousData,
    // Retry failed requests (network resilience)
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    ...options,
  })

  // Flatten all pages into a single array
  const allEconomicCycles =
    (
      queryResult.data as InfiniteData<EconomicCyclesResponse> | undefined
    )?.pages?.flatMap((page: EconomicCyclesResponse) => page?.items || []) || []

  // Get total from first page (all pages have same total)
  const totalItems =
    (queryResult.data as InfiniteData<EconomicCyclesResponse> | undefined)
      ?.pages?.[0]?.totalItems || 0

  return {
    economicCycles: allEconomicCycles,
    totalItems,
    loadedCount: allEconomicCycles?.length || 0,
    hasNextPage: queryResult.hasNextPage,
    isLoading: queryResult.isLoading,
    isLoadingMore: queryResult.isFetchingNextPage,
    isError: queryResult.isError,
    error: queryResult.error,
    fetchNextPage: queryResult.fetchNextPage,
    refetch: queryResult.refetch,
  }
}

export const getEconomicCyclesQueryKey = ({
  businessId,
  dateFrom,
  dateTo,
  economicCycleId,
  page,
  per_page,
}: EconomicCyclesQueryKeyParams): [string, EconomicCyclesQueryKeyParams] => [
  ECONOMIC_CYCLES_QUERY_KEY,
  { businessId, dateFrom, dateTo, economicCycleId, page, per_page },
]
